import React from "react";
import PropTypes from "prop-types";
import { TextField, MenuItem, Box, Typography } from "@mui/material";
import {
  NumberFormatCustom,
  parseCurrencyToNumber,
  safeNumber,
} from "../formHelpers";
import { useBondBasicInformationCore } from "../hooks/useBondBasicInformationCore";
import {
  renderBondSubCategory,
  renderCounty,
  renderProbateCountyFields,
  renderNameFields,
} from "./basicInformationRenderHelpers";

function renderSplitByRepresentatives(config, data, errors, handleChange) {
  if (!config.splitByRepresentatives) return null;
  const principalCount = Number.parseInt(data.PrincipalNumber, 10);
  if (!principalCount || principalCount <= 1) return null;
  return (
    <TextField
      select
      label="Do we need individual bonds for each personal representative?"
      name="is_split_by_personal_representatives"
      value={data.is_split_by_personal_representatives || ""}
      onChange={handleChange}
      fullWidth
      required
      error={!!errors.is_split_by_personal_representatives}
      helperText={errors.is_split_by_personal_representatives}
    >
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </TextField>
  );
}

function renderEsignatureField(config, data, errors, handleChange) {
  const { esignatureMode, esignatureThreshold = 50001 } = config;
  if (esignatureMode === "none") return null;
  if (esignatureMode === "threshold") {
    const bondAmount = safeNumber(parseCurrencyToNumber(data.bondAmount));
    if (bondAmount < esignatureThreshold) return null;
  }
  return (
    <TextField
      select
      label="Will you prefer to e-sign the Indemnity Agreement?"
      name="is_esignatures"
      value={data.is_esignatures || "No"}
      onChange={handleChange}
      fullWidth
      required
      error={!!errors.is_esignatures}
      helperText={errors.is_esignatures}
    >
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </TextField>
  );
}

function getContainerClassName(config, layoutView) {
  if (config.supportsLayoutView) {
    return `${layoutView === "horizontal" ? "max-w-full" : "max-w-3xl"} bg-gray-100 p-6 rounded-lg shadow-md mx-auto mt-8`;
  }
  return "bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8";
}

function getGridClassName(config, layoutView) {
  if (config.supportsLayoutView && layoutView === "horizontal") {
    return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6";
  }
  if (config.supportsLayoutView) {
    return "grid grid-cols-1 gap-y-6";
  }
  return "grid grid-cols-1 gap-4";
}

const BondBasicInformationBase = ({
  config,
  globalData = {},
  data = {},
  updateData,
  errors = {},
  layoutView,
  surface = "creation",
}) => {
  const {
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
    handleCourtCaseToggle,
  } = useBondBasicInformationCore({ data, updateData, config, surface });

  const { dateField, principalField, courtCaseNumberLabel } = config;
  const isHistoric = surface === "historic";

  return (
    <Box
      className={getContainerClassName(config, layoutView)}
      data-testid="bond-wizard-step-basic"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>

      <Box className={getGridClassName(config, layoutView)}>
        {renderBondSubCategory(config, data, errors, handleAutocompleteChange)}
        {renderCounty(config, data, errors, counties, loadingCounties, handleAutocompleteChange)}
        {renderProbateCountyFields(config, data, handleChange)}
        {renderNameFields(config, data, errors, handleChange, handleAutocompleteChange, {
          showAliasesAka: !isHistoric,
        })}
        <TextField
          label={dateField.label}
          name={dateField.name}
          value={data[dateField.name] || ""}
          onChange={handleChange}
          type="date"
          fullWidth
          required
          error={!!errors[dateField.name]}
          helperText={errors[dateField.name]}
          slotProps={{
            inputLabel: { shrink: true },
            ...(isHistoric
              ? {}
              : {
                  htmlInput: {
                    max: new Date().toISOString().split("T")[0],
                  },
                }),
          }}
        />
        <TextField
          label="Bond Amount"
          name="bondAmount"
          value={data.bondAmount || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bondAmount}
          helperText={errors.bondAmount}
          slotProps={{ input: { inputComponent: NumberFormatCustom } }}
        />
        <TextField
          label="Premium Amount"
          name="premiumAmount"
          value={formatAsCurrency(data.premiumAmount)}
          fullWidth
          required
          disabled
        />
        <TextField
          select
          label="Do you have an official court case number?"
          name="hasCourtCaseNumber"
          value={data.hasCourtCaseNumber || ""}
          onChange={handleCourtCaseToggle}
          fullWidth
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.hasCourtCaseNumber === "Yes" ? (
          <TextField
            label={courtCaseNumberLabel}
            name="courtCaseNumber"
            value={data.courtCaseNumber || ""}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.courtCaseNumber}
            helperText={errors.courtCaseNumber}
            slotProps={{ htmlInput: { maxLength: 30 } }}
          />
        ) : null}
        <TextField
          label={principalField.label}
          name={principalField.name}
          value={data[principalField.name] || 1}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors[principalField.name]}
          helperText={errors[principalField.name]}
          type="number"
          slotProps={{ input: { inputProps: { min: 1, max: 4 } } }}
        />
        {renderSplitByRepresentatives(config, data, errors, handleChange)}
        {!isHistoric && renderEsignatureField(config, data, errors, handleChange)}
      </Box>
    </Box>
  );
};

BondBasicInformationBase.propTypes = {
  config: PropTypes.object.isRequired,
  globalData: PropTypes.object,
  data: PropTypes.object,
  updateData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  layoutView: PropTypes.string,
  surface: PropTypes.oneOf(["creation", "historic"]),
};

export default BondBasicInformationBase;
